<script lang="ts" setup>
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { NButton, NIcon, NTooltip } from 'naive-ui';
import { Open16Regular } from '@vicons/fluent';
import ChassisNumberRender from '@/components/chassis-number/ChassisNumberRender.vue';
import type { PropType } from 'vue';
import type { VehicleIdentifierPlateSize } from '@/constants/vehicle-identifier-plate-size';

const router = useRouter();
const { t } = useI18n();

const props = defineProps({
  chassisNumber: {
    type: String,
    required: false,
  },
  hasLinkToAssignment: {
    type: Boolean,
    required: false,
    default: false,
  },
  assignmentId: {
    type: Number,
    required: false,
  },
  hasLinkToVehicle: {
    type: Boolean,
    required: false,
    default: false,
  },
  vehicleId: {
    type: Number,
    required: false,
  },
  size: {
    type: String as PropType<VehicleIdentifierPlateSize>,
    default: 'medium',
    required: false,
  },
  readOnly: {
    type: Boolean,
    default: false,
    required: false,
  },
  truncate: {
    type: Boolean,
    default: true,
    required: false,
  },
});

const showAssignmentLink = () =>
    props.hasLinkToAssignment && !!props.assignmentId && !props.readOnly;

const showVehicleLink = () =>
    props.hasLinkToVehicle && !!props.vehicleId && !props.readOnly;

const navigateToAssignment = () => {
  if (!props.hasLinkToAssignment || !props.assignmentId) {
    return;
  }
  const routeData = router.resolve(`/assignment/${props.assignmentId}`);
  window.open(routeData.href, '_blank');
};

const navigateToVehicle = () => {
  if (!props.hasLinkToVehicle || !props.vehicleId) {
    return;
  }
  router.push(`/vehicle/${props.vehicleId}`);
};
</script>

<template>
  <div class="chassis-number-button-root">
    <chassis-number-render
        :chassis-number="props.chassisNumber"
        :size="props.size"
        :read-only="props.readOnly"
        :truncate="props.truncate"
    />
    <n-tooltip v-if="showAssignmentLink()">
      <template #trigger>
        <n-button
            class="chassis-number-open-assignment-button"
            circle
            size="small"
            quaternary
            @click.stop.prevent="navigateToAssignment"
        >
          <template #icon>
            <n-icon>
              <Open16Regular />
            </n-icon>
          </template>
        </n-button>
      </template>
      {{ t('components.license_plate.tooltip.open_assignment') }}
    </n-tooltip>
    <n-tooltip v-if="showVehicleLink()">
      <template #trigger>
        <n-button
            class="chassis-number-open-vehicle-button"
            circle
            size="small"
            quaternary
            @click.stop.prevent="navigateToVehicle"
        >
          <template #icon>
            <n-icon>
              <Open16Regular />
            </n-icon>
          </template>
        </n-button>
      </template>
      {{ t('components.license_plate.tooltip.open_vehicle') }}
    </n-tooltip>
  </div>
</template>

<style lang="scss" scoped>
.chassis-number-button-root {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: var(--dis-space-0_5x, 4px);
}

.chassis-number-button-root > :deep(.vehicle-identification-plate-wrapper) {
  flex: 0 0 auto;
}

.chassis-number-open-assignment-button,
.chassis-number-open-vehicle-button {
  flex: 0 0 auto;
}
</style>
